import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  GetAccountQuery,
  GetAccountQueryVariables,
} from "types/graphql-schema";

import AiPreferencesPage from "@apps/account/components/ai/ai-preferences-page";
import AiSkillsPage from "@apps/account/components/ai/ai-skills-page";
import getAccountQuery from "@apps/account/graphql/get-account-query";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import useDocumentTitle from "@components/use-document-title/use-document-title";

import ProfileResponsiveTabs from "../profile/profile-responsive-tabs";
import RoutinesList from "./routines-list";

const AgentPage = () => {
  useDocumentTitle("Agent");
  const { path } = useRouteMatch();
  const location = useLocation();
  const link = useLink();
  const routinesEnabled = waffle.flag_is_active("ask-ai-scheduled-prompts");

  const { loading, error, data } = useQuery<
    GetAccountQuery,
    GetAccountQueryVariables
  >(getAccountQuery, { fetchPolicy: "network-only" });

  const defaultTab = routinesEnabled ? "routines" : "skills";
  // A tab is active on its own sub-routes, and the default tab is also active
  // on the bare /agent path that redirects to it.
  const isAgentRoot = location.pathname === "/agent";
  const tabActive = (value: string) =>
    location.pathname.startsWith(`/agent/${value}`) ||
    (isAgentRoot && value === defaultTab);

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    routinesEnabled && {
      value: "routines",
      title: "Routines",
      active: tabActive("routines"),
    },
    {
      value: "skills",
      title: "Skills",
      active: tabActive("skills"),
    },
    {
      value: "preferences",
      title: "Preferences",
      active: tabActive("preferences"),
    },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    link.redirect(`/agent/${button.value}`);
  };

  return (
    <Layout>
      <Layout.Header
        className="bg-[#FCFCFD]"
        title={
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Button
              size={ButtonSize.medium}
              theme={buttonTheme.text}
              to="/agent"
            >
              Agent
            </Button>
            <ProfileResponsiveTabs
              buttons={tabButtons}
              onClick={handleTabClick}
            />
          </div>
        }
      />
      <Layout.Container>
        <Layout.Main width="md" className="bg-[#FCFCFD]">
          {error ? (
            <GraphqlError
              error={error}
              whatDidNotWork="Your account could not be loaded."
            />
          ) : loading || !data ? (
            <Loading />
          ) : (
            <Switch>
              <Route exact path={path}>
                <Redirect to={`/agent/${defaultTab}`} />
              </Route>
              <Route exact path={`${path}/skills`}>
                <AiSkillsPage />
              </Route>
              <Route exact path={`${path}/routines`}>
                {routinesEnabled && data.me ? (
                  <RoutinesList />
                ) : (
                  <Redirect to="/agent/skills" />
                )}
              </Route>
              <Route exact path={`${path}/preferences`}>
                <AiPreferencesPage me={data.me} />
              </Route>
              <Route>
                <Redirect to="/agent" />
              </Route>
            </Switch>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default AgentPage;
